import React, { useMemo } from 'react';

import {bindActionCreators} from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';

import 'materialize-css';
import M from 'materialize-css';
import '../styles/report.scss';

import ReportComponents from './report_components/ReportComponents';
import { groupByPosition } from './orderByPosition';
import ReportTest from './report_components/ReportTest';
import ReportNote from './report_components/ReportNote';
import NodeWorkbench from './report_components/node_workbench/NodeWorkbench';
import RecipientSelect from '../components/RecipientSelect';
import PreSubmit from './report_components/PreSubmit';
import SubmitSuccess from './report_components/SubmitSuccess';
import ReportChanges from './report_components/ReportChanges';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ColorSchemePicker from '../components/ColorSchemePicker';
import { reportViewUrl } from '../utils/reportUrls';
import { Checkbox } from 'react-materialize';

import CreateIcon from '@mui/icons-material/Create';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Textfit from 'react-textfit';
import Loading from '../Loading';
import PaletteIcon from '@mui/icons-material/Palette';
import EditIcon from '@mui/icons-material/Edit';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { ReactComponent as ProasysOLogo } from './proasys_o_mono.svg';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import DraggableGrid, { DraggableItem } from "ruuri";
import { sample, random, uniqueId } from "lodash-es";
import { useState, useEffect, useRef, useCallback } from 'react';
import { debounce } from 'lodash-es';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Wrapper around a single test/note row that hooks into @dnd-kit's
// SortableContext. The drag handle (and therefore the activator) is
// only rendered when its component is in edit mode, which gates
// reordering to that state.
function SortableReportNode({ node, component, isEditing, log, setNodeFocused, setComponentFocused }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `node:${node.id}`,
    data: { nodeId: node.id, componentId: component.id, iAm: node.iAm },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="dragging-container">
      {isEditing && (
        <div
          className="drag-handle"
          // touch-action: none is required for dnd-kit's TouchSensor; without
          // it mobile browsers swallow the touch as a scroll gesture and the
          // drag never activates. The sidebar's drag handle gets this from
          // a deeply scoped scss rule, but the report-body .drag-handle
          // doesn't, so set it inline to be cascade-independent.
          style={{ touchAction: 'none' }}
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
        >
          <DragHandleIcon />
        </div>
      )}
      {node.iAm === 'test' ? (
        <ReportTest test={node} component={component} log={log} />
      ) : (
        <ReportNote
          node={node}
          component={component}
          log={log}
          handleClick={() => {
            setNodeFocused(node);
            setComponentFocused(component);
          }}
        />
      )}
    </div>
  );
}

const ReportEdit = (props) => {
  const [editMetadata, setEditMetadata] = useState(false);
  const [hideSidebar, setHideSidebar] = useState(false);
  const [componentFocused, setComponentFocused] = useState(null);
  const [nodeFocused, setNodeFocused] = useState(null); // used by nodeworkbench
  const [addIntent, setAddIntent] = useState(null); // which node type the add row requested
  const [pickersInitialized, setPickersInitialized] = useState(false);
  const [preSubmitShown, setPreSubmitShown] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [changesShown, setChangesShown] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(50); // Changed from headerShown boolean to headerHeight number
  const [tempRecipients, setTempRecipients] = useState(null);
  const [componentEditing, setComponentEditing] = useState(null);
  // Pending node reorder buffer keyed by componentId. Each entry is the
  // ordered list of node ids the user has dragged into place but not yet
  // committed to the server. Cleared when the component's edit-mode
  // submit (the check button) flushes the changes.
  const [pendingNodeOrders, setPendingNodeOrders] = useState({});
  const [showPreviousLogs, setShowPreviousLogs] = useState(false);
  const [activeComponentId, setActiveComponentId] = useState(null);
  const [fieldStates, setFieldStates] = useState({
    date_of_visit: { isSaving: false, isSaved: false },
    onsite_start: { isSaving: false, isSaved: false },
    onsite_end: { isSaving: false, isSaved: false },
    title: { isSaving: false, isSaved: false },
  });

  const scrollPosition = useRef(0);
  const reportBodyRef = useRef(null);
  const scrollingToId = useRef(null);

  useEffect(() => {
    props.showNav(true);
    props.fetchReport(
      props.authToken,
      window.location.search.split('?')[1]
    );
    props.fetchTestFields(props.authToken);
  }, []);

  useEffect(() => {
    if (!props.report) return;
    
    let datepickers = document.querySelectorAll('.datepicker-footer');
    let datepickerOptions = {
      autoClose: true,
      format: 'mmm d, yyyy',
      defaultDate: new Date(props.report.date_of_visit),
      setDefaultDate: true,
      container: document.querySelectorAll('.report-body'),
      maxDate: new Date(),
      onSelect: (d) => {
        setFieldStates(prev => ({
          ...prev,
          date_of_visit: { isSaving: true, isSaved: false }
        }));
        props.updateReport({
          id: props.report.id,
          date_of_visit: d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
        }, {fridge: true, freezer: props.authToken}, () => {
          handleSaveConfirmation(['date_of_visit']);
        })
      }
    };
    let dInstance = M.Datepicker.init(datepickers, datepickerOptions)

    let timepickers = document.querySelectorAll('.timepicker-footer');
    let timepickerOptions = {
      autoClose: true,
      container: '.report-body',
      defaultTime: 'now',
      onCloseEnd: () => {
        let times = Array.prototype.slice.call(timepickers).map(picker => {
          return picker.value
        });

        setFieldStates(prev => ({
          ...prev,
          onsite_start: { isSaving: true, isSaved: false },
          onsite_end: { isSaving: true, isSaved: false }
        }));

        props.updateReport({
          id: props.report.id,
          onsite_start: times[0].replace(/^0+/, ''),
          onsite_end: times[1].replace(/^0+/, '')
        }, {fridge: true, freezer: props.authToken}, () => {
          handleSaveConfirmation(['onsite_start', 'onsite_end']);
        });
      }
    }
    let tInstance = M.Timepicker.init(timepickers, timepickerOptions);

    // Cleanup function to destroy picker instances when component unmounts or report changes.
    // Operator reports have no onsite time inputs, so either list can be empty.
    return () => {
      datepickers.forEach((el) => M.Datepicker.getInstance(el)?.destroy());
      timepickers.forEach((el) => M.Timepicker.getInstance(el)?.destroy());
    }
  }, [props.report]);

  const validateResult = (result) => {
    return result?.replace(/[^0-9.]/g,'');
  };

  const handleScroll = (e) => {
    const scrollingUp = e.deltaY < 0;
    const scrollDelta = e.currentTarget.scrollTop - scrollPosition.current;
    
    if (Math.abs(scrollDelta) >= 1) { // Reduced threshold for smoother response
      const maxHeaderHeight = 50;
      const scrollThreshold = 40; // Amount of scroll needed for full expansion/collapse
      
      if (scrollingUp) {
        // Expanding header - calculate proportional height
        const expansionAmount = Math.min(Math.abs(scrollDelta), scrollThreshold);
        const heightIncrease = (expansionAmount / scrollThreshold) * maxHeaderHeight;
        const newHeight = Math.min(headerHeight + heightIncrease, maxHeaderHeight);
        setHeaderHeight(newHeight);
      } else {
        // Collapsing header - calculate proportional height
        const collapseAmount = Math.min(Math.abs(scrollDelta), scrollThreshold);
        const heightDecrease = (collapseAmount / scrollThreshold) * maxHeaderHeight;
        const newHeight = Math.max(headerHeight - heightDecrease, 0);
        setHeaderHeight(newHeight);
      }
      
      scrollPosition.current = e.currentTarget.scrollTop;
    }
  };

  const toggleSidebar = () => {
    setHideSidebar(!hideSidebar);
  };

  const formatLimitsText = (min, max) => {
    if (min && max) {
      return <span className="limits">{` (${min} — ${max})`}</span>
    } else if (min) {
      return <span className="limits">{` (min ${min})`}</span>
    } else if (max) {
      return <span className="limits">{` (max ${max})`}</span>
    } else {
      return null
    }
  };

  const syncActiveFromScroll = useCallback((container) => {
    if (!container) return;

    if (scrollingToId.current != null) {
      const target = container.querySelector(
        `.report-results-container[data-component-id="${scrollingToId.current}"]`
      );
      if (!target) {
        scrollingToId.current = null;
      } else {
        const delta = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
        if (Math.abs(delta) > 48) return;
        scrollingToId.current = null;
      }
    }

    const sections = [...container.querySelectorAll('.report-results-container[data-component-id]')]
      .filter((section) => !section.classList.contains('hidden') && !section.classList.contains('service-log'));
    if (!sections.length) return;

    const rootTop = container.getBoundingClientRect().top;
    const offset = 32;
    let current = sections[0];
    for (const section of sections) {
      if (section.getBoundingClientRect().top - rootTop <= offset) current = section;
      else break;
    }

    const id = current.getAttribute('data-component-id');
    setActiveComponentId((prev) => (`${prev}` === `${id}` ? prev : id));
  }, []);

  useEffect(() => {
    const el = reportBodyRef.current;
    if (!el) return undefined;

    const onScroll = () => syncActiveFromScroll(el);
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => el.removeEventListener('scroll', onScroll);
  }, [props.report, syncActiveFromScroll]);

  const scrollToComponent = (id) => {
    const node = document.getElementById(`comp-${id}`);
    if (!node) return;

    scrollingToId.current = id;
    setActiveComponentId(id);
    node.scrollIntoView({behavior: 'smooth', block: 'start'});
    window.setTimeout(() => {
      if (scrollingToId.current === id) scrollingToId.current = null;
    }, 700);
  };

  const toggleComponentVisibility = (id, type) => {
    let hiddenComps = [...(props.report.hidden_components || [])];
    // console.log('hiddenComps', hiddenComps, id, type);
    if (type === 'group') {
      let comps = Object.entries(props.report.components).map(c => c[1]).filter(c => c.group === id);
      let visible = !comps.every(c => hiddenComps.includes(`${c.id}`));

      if (visible) {
        hiddenComps = hiddenComps.concat(comps.map(c => `${c.id}`));
      } else {
        hiddenComps = hiddenComps.filter(c => !comps.map(c => `${c.id}`).includes(c));
      }
    } else if (type === 'component') {
      let visible = !hiddenComps.includes(`${id}`);

      if (visible) {
        hiddenComps = hiddenComps.concat(`${id}`);
      } else {
        hiddenComps = hiddenComps.filter(c => c !== `${id}`);
      }
    }

    // console.log('updated hiddenComps', hiddenComps);

    props.updateReport(
      {id: props.report.id, hidden_components: hiddenComps},
      {fridge: true, freezer: props.authToken},
      null
    );
  };

  const formatSubmittedAt = (submitted) => {
    if (!submitted) return '';
    return new Date(submitted).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const showChangesButton = Boolean(props.report?.id) && Boolean(props.user) && !props.user.operator;
  // Operators fill in readings, notes and photos; the layout follows their
  // template. The server enforces this too.
  const layoutLocked = Boolean(props.user?.operator);

  const generateAuthorText = () => {
    if (props.report.submitted) {
      const submitted = new Date(props.report.submitted);
      const isToday = submitted.toLocaleDateString() === new Date().toLocaleDateString();
      const submitTime = submitted.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});

      if (isToday) {
        return `Submitted at ${submitTime} by ${props.report.author}`;
      }
      return `Submitted ${formatSubmittedAt(props.report.submitted)} by ${props.report.author}`;
    }
    return `Report by ${props.report.author}`;
  };

  const orderedComponentNodes = (componentId) => {
    let component = props.report.components[componentId];
    if (!component) {
      return [];
    }
    let tests = (Object.entries(component.tests || {}) || []).map(t => t[1]);
    let notes = (Object.entries(component.notes || {}) || []).map(n => n[1]);
    let allNodes = [...notes, ...tests].filter((n) => !n.deleted);

    // If the user has dragged nodes around in this component but not yet
    // submitted, render them in that pending order so the UI tracks the
    // drop without round-tripping through the server.
    const pendingOrder = pendingNodeOrders[componentId];
    if (pendingOrder && pendingOrder.length > 0) {
      const orderIndex = new Map(pendingOrder.map((id, i) => [`${id}`, i]));
      return allNodes.sort((a, b) => {
        const ai = orderIndex.has(`${a.id}`) ? orderIndex.get(`${a.id}`) : Infinity;
        const bi = orderIndex.has(`${b.id}`) ? orderIndex.get(`${b.id}`) : Infinity;
        if (ai !== bi) return ai - bi;
        return (a.position ?? Infinity) - (b.position ?? Infinity);
      });
    }

    return allNodes.sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity));
  };

  const orderedBodyComponents = () => {
    if (!props.report?.components) return [];

    const entries = Object.entries(props.report.components).filter((c) => !c[1].deleted);
    const isGeneral = ([k, v]) => `${v.id}` === '0' || k === '0';
    const isLog = ([k, v]) => `${v.id}` === '-1' || k === '-1';

    // Same order as the sidebar: by position, each group kept together.
    return [
      ...entries.filter(isGeneral),
      ...groupByPosition(
        entries.filter((e) => !isGeneral(e) && !isLog(e)),
        ([, v]) => v.position,
        ([, v]) => v.group || ''
      ).flatMap(([, items]) => items),
      ...entries.filter(isLog),
    ];
  };

  // Sensors shared across every per-component DndContext so the same touch
  // / pointer activation thresholds apply everywhere in the report body.
  const nodeSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  // Drag end only updates the local pending order; the actual position
  // patches are sent to the server when the component's edit submit runs.
  const handleNodeDragEnd = (componentId) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeNodeId = active.data.current?.nodeId;
    const overNodeId = over.data.current?.nodeId;
    if (activeNodeId == null || overNodeId == null) return;

    const nodes = orderedComponentNodes(componentId);
    const activeIdx = nodes.findIndex((n) => `${n.id}` === `${activeNodeId}`);
    const overIdx = nodes.findIndex((n) => `${n.id}` === `${overNodeId}`);
    if (activeIdx === -1 || overIdx === -1) return;

    const reordered = arrayMove(nodes, activeIdx, overIdx);
    setPendingNodeOrders((prev) => ({
      ...prev,
      [componentId]: reordered.map((n) => n.id),
    }));
  };

  // Convert the locally-pending node order for `componentId` into a
  // tests/notes patch keyed by node id and return it for inclusion in a
  // larger updateReport payload. Returns null when nothing is pending or
  // the order matches the server's current positions.
  const buildPendingNodeOrderPatch = (componentId) => {
    const order = pendingNodeOrders[componentId];
    if (!order || order.length === 0) return null;

    const component = props.report.components[componentId];
    if (!component) return null;

    const nodesById = {};
    Object.values(component.tests || {}).forEach((n) => { nodesById[`${n.id}`] = n; });
    Object.values(component.notes || {}).forEach((n) => { nodesById[`${n.id}`] = n; });

    const tests = {};
    const notes = {};
    let changed = false;
    order.forEach((nodeId, i) => {
      const node = nodesById[`${nodeId}`];
      if (!node || node.deleted) return;
      const newPosition = i + 1;
      if (node.position === newPosition) return;
      changed = true;
      const patch = {
        id: node.id,
        position: newPosition,
        iAm: node.iAm,
        synced: false,
        ...(node.breadcrumbs ? { breadcrumbs: node.breadcrumbs } : {}),
      };
      if (node.iAm === 'test') tests[node.id] = patch;
      else notes[node.id] = patch;
    });

    if (!changed) return null;
    return { tests, notes };
  };

  const clearPendingNodeOrder = (componentId) => {
    setPendingNodeOrders((prev) => {
      if (!(componentId in prev)) return prev;
      const next = { ...prev };
      delete next[componentId];
      return next;
    });
  };

  const handleComponentEditSubmit = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const compId = componentEditing.id;
    const currentName = props.report.components[compId]?.name;
    const tempName = componentEditing.tempName;
    const nameChanged = tempName && tempName !== currentName;

    const orderPatch = buildPendingNodeOrderPatch(compId);

    // Nothing to submit; just close edit mode and drop any stale pending
    // order (it would have matched current positions, see the patch builder).
    if (!nameChanged && !orderPatch) {
      clearPendingNodeOrder(compId);
      setComponentEditing(null);
      return;
    }

    const componentPatch = {};
    if (nameChanged) componentPatch.name = tempName;
    if (orderPatch) Object.assign(componentPatch, orderPatch);

    props.updateReport(
      {
        id: props.report.id,
        components: { [compId]: componentPatch },
      },
      { fridge: true, freezer: props.authToken },
      () => {
        clearPendingNodeOrder(compId);
        setComponentEditing(null);
      }
    );
  };

  const componentIsVisible = (comp) => {
    let hidden = props.report.hidden_components || [];
    return !hidden.includes(`${comp.id}`);
  };

  const componentGroupIsVisible = (groupName) => {
    let group = Object.entries(props.report.components).map(c => c[1]).filter(c => c.group === groupName);
    return group.filter(c => !componentIsVisible(c)).length < group.length;
  };

  const openWorkbench = (component, intent) => {
    setAddIntent(intent);
    setComponentFocused(component);
  };

  const renderAddNodeRow = (component) => (
    <div className="node-container" data-type="pseudonode">
      <div className="add-node-row">
        {!layoutLocked && (
          <button
            type="button"
            className="add-node"
            data-node="test"
            data-comp={component.id}
            onClick={() => openWorkbench(component, 'test')}
          >
            <ScienceOutlinedIcon className="add-node-icon" />
            Test
          </button>
        )}

        <button
          type="button"
          className="add-node"
          data-node="note"
          data-comp={component.id}
          onClick={() => openWorkbench(component, 'note')}
        >
          <NotesOutlinedIcon className="add-node-icon" />
          Note
        </button>

        <button
          type="button"
          className="add-node"
          data-node="image"
          data-comp={component.id}
          onClick={() => openWorkbench(component, 'image')}
        >
          <AttachFileIcon className="add-node-icon" />
          Attach
        </button>
      </div>
    </div>
  );

  if (!props.report) {
    return (  
      <div className="loading-container" style={{position: 'absolute', top: '100px', left: '50%', transform: 'translateX(-50%)'}}>
        <Loading loading={props.loading} loadingMessage='Loading report...' height='120px'/>
      </div>
    )
  }

  const handleComponentsUpdate = (components) => {
    const originals = props.report.components || {};

    const componentUpdates = components.reduce((acc, comp) => {
      const original = originals[comp.id] || originals[`${comp.id}`];
      const changed = !original ||
        !!comp.deleted ||
        original.position != comp.position ||
        original.group != comp.group ||
        original.name != comp.name;

      acc[comp.id] = {
        position: comp.position,
        name: comp.name,
        group: comp.group,
      };
      if (comp.deleted) acc[comp.id].deleted = true;
      if (changed) acc[comp.id].synced = false;
      return acc;
    }, {});

    // Update report with new component positions
    props.updateReport(
      {
        id: `${props.report.id}`,
        timestamp: Date.now(),
        components: componentUpdates
      },
      {fridge: true, freezer: props.authToken},
      null
    );
  };

  const handleSaveConfirmation = (fieldsArray) => {
    // Reset all fields
    setFieldStates(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = { isSaving: false, isSaved: false };
      });
      return newState;
    });

    // Set saved state for specified fields
    setFieldStates(prev => {
      const newState = { ...prev };
      fieldsArray.forEach(field => {
        newState[field] = { isSaving: false, isSaved: true };
      });
      return newState;
    });
    
    setTimeout(() => {
      setFieldStates(prev => {
        const newState = { ...prev };
        fieldsArray.forEach(field => {
          newState[field] = { isSaving: false, isSaved: false };
        });
        return newState;
      });
    }, 2000);
  };

  return (
    <>
      <div className={`app-container`}>
        <div className={`loading-container${showChangesButton ? ' beside-changes-button' : ''}`}>
          <Loading loading={props.loading} location={{top: '5px', right: '5px'}} height='60px'/>
        </div>

        <div
          className={`report-header${props.report.submitted ? ' is-submitted' : ''}`}
          style={{ height: `${headerHeight}px` }}
        >
          <a
            className="report-header-logo"
            href="/reports"
            aria-label="Back to reports"
            title="Back to reports"
          >
            <ProasysOLogo className="report-header-logo-mark" aria-hidden="true" />
          </a>

          <div className="report-header-main">
            {editTitle === 'top' ? (
              <form
                className="report-header-title-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  props.updateReport(
                    {id: props.report.id, title: props.report.title},
                    {fridge: true, freezer: props.authToken},
                    () => setEditTitle(null)
                  );
                }}
              >
                <input
                  type="text"
                  id="report-title-input"
                  className="report-header-title-input browser-default"
                  defaultValue={props.report?.title}
                  placeholder="Report title"
                  autoFocus
                  onChange={(e) => props.updateReport(
                    {id: props.report.id, title: e.target.value},
                    {fridge: true},
                    null
                  )}
                  onBlur={() => {
                    setFieldStates((prev) => ({
                      ...prev,
                      title: { isSaving: true, isSaved: false }
                    }));
                    props.updateReport(
                      {id: props.report.id, title: props.report.title},
                      {fridge: true, freezer: props.authToken},
                      () => {
                        setEditTitle(null);
                        handleSaveConfirmation(['title']);
                      }
                    );
                  }}
                />
                <button type="submit" className="report-header-icon-btn" aria-label="Save title">
                  <CheckRoundedIcon />
                </button>
              </form>
            ) : (
              <button
                type="button"
                className="report-header-title"
                onClick={() => setEditTitle('top')}
              >
                <span className="report-header-title-text">{props.report?.title}</span>
                <EditOutlinedIcon className="report-header-title-edit" />
                <div className={`saved-indicator ${fieldStates.title.isSaved ? 'shown' : ''}`}>
                  <CheckIcon />
                </div>
                {fieldStates.title.isSaving && (
                  <div className="saving-flash shown" />
                )}
              </button>
            )}
            {props.report.submitted ? (
              <span className="report-header-submitted-at">
                Submitted {formatSubmittedAt(props.report.submitted)} by {props.report.author}
              </span>
            ) : null}
          </div>

          {showChangesButton && (
            <button
              type="button"
              className="report-header-changes"
              onClick={() => setChangesShown(true)}
              title="Report history"
            >
              <HistoryRoundedIcon />
            </button>
          )}
        </div>

        <div className="content-container">
          <div className={`components-sidebar ${hideSidebar ? 'hidden' : ''}`}>

            <div
              className="sidebar-toggle"
              onClick={toggleSidebar}
              role="button"
              aria-label="Toggle systems panel"
              title="Toggle systems panel"
            >
              <KeyboardDoubleArrowLeftRoundedIcon />
            </div>

            <ReportComponents
              components={Object.entries(props.report.components).map(c => c[1]).filter(c => !c.deleted)}
              hiddenComponents={props.report.hidden_components}
              updateHiddenComponents={toggleComponentVisibility}
              onUpdate={handleComponentsUpdate}
              activeComponentId={activeComponentId}
              onSelectComponent={scrollToComponent}
              locked={layoutLocked}
            />

            <div className="report-buttons-container">
              <div className="report-buttons-secondary">
                <a
                  className="sidebar-btn"
                  href={reportViewUrl(props.report.hash_id) || props.report.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <OpenInNewRoundedIcon />
                  Preview
                </a>
                <button
                  type="button"
                  className="sidebar-btn"
                  onClick={() => setTempRecipients(props.report.recipients)}
                >
                  <PeopleAltOutlinedIcon />
                  Recipients
                  {props.report.recipients?.length ? (
                    <span className="sidebar-btn-count">{props.report.recipients.length}</span>
                  ) : null}
                </button>
              </div>
              <button
                type="button"
                className="sidebar-btn sidebar-btn--primary"
                onClick={() => setPreSubmitShown(true)}
              >
                <CheckRoundedIcon />
                Finalize
              </button>
            </div>
          </div>

          <div className="report-body" ref={reportBodyRef} onWheel={handleScroll}>
            {orderedBodyComponents().map((c) => c[0] !== '-1' ? (
              <div key={`comp-${c[1].id}`} id={`comp-${c[1].id}`} data-component-id={c[1].id} className={`report-results-container ${componentIsVisible(c[1]) ? '' : 'hidden'}`}>
                <div className="report-component-header">
                  <div className="header-content" onClick={() => !layoutLocked && setComponentEditing({id: c[1].id, tempName: c[1].name})}>
                    {componentEditing?.id === c[1].id ? (
                      <form onSubmit={handleComponentEditSubmit}>
                        <input
                          type="text"
                          className="component-name-input" 
                          defaultValue={c[1].name}
                          onChange={(e) => setComponentEditing({id: c[1].id, tempName: e.target.value})} 
                          onBlur={(e) => handleComponentEditSubmit(e)}
                        />
                        <button type="submit" style={{display:'none'}}>submit</button>
                      </form>
                    ) : (
                      <Textfit className="component-name" mode="multi" max={16} style={{width: '100%'}}>{c[1].name}</Textfit>
                    )}
                    {layoutLocked ? null : componentEditing?.id === c[1].id ? (
                      <button className="edit-button submit-button" title="Edit Component Name" onClick={(e) => handleComponentEditSubmit(e)}>
                        <CheckIcon />
                      </button>
                    ) : (
                      <button className="edit-button" title="Edit Component Name" onClick={() => setComponentEditing({id: c[1].id, tempName: c[1].name})}>
                        <EditIcon />
                      </button>
                    )}
                  </div>
                </div>
                
                <DndContext
                  sensors={nodeSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleNodeDragEnd(c[1].id)}
                >
                  <SortableContext
                    items={orderedComponentNodes(c[1].id).map((n) => `node:${n.id}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedComponentNodes(c[1].id).map((node) => (
                      <SortableReportNode
                        key={node.id}
                        node={node}
                        component={c[1]}
                        isEditing={componentEditing?.id === c[1].id}
                        setNodeFocused={setNodeFocused}
                        setComponentFocused={setComponentFocused}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                
                {renderAddNodeRow(c[1])}
              </div>
            ) : null)}

            {props.report.components['-1'] && (
            <div key={`comp--1`} id={`comp--1`} className="report-results-container service-log">
              <div className="active-component-header">
                <div className="header-content">
                  <span className="service-log-title">Service Log</span>
                  <span className="service-log-subtitle">Visible only to ProAsys</span>
                </div>
              </div>

              {(() => {
                const todayKey = new Date().toLocaleString().split(',')[0];
                const dateEntries = Object.entries(props.report.components['-1'].nodes).reverse();
                const hasToday = dateEntries.some(([date]) => date === todayKey);
                const previousEntries = dateEntries.filter(([date]) => date !== todayKey);
                const todayEntry = dateEntries.find(([date]) => date === todayKey);

                const addNodeButton = renderAddNodeRow(props.report.components['-1']);

                const renderNodes = (nodes) => Object.entries(nodes).map(([, node]) => (
                  node.iAm === 'test' ?
                    <ReportTest
                      key={node.id}
                      test={node}
                      component={props.report.components['-1']}
                      log={true}
                    /> :
                    <ReportNote
                      key={node.id}
                      node={node}
                      component={props.report.components['-1']}
                      handleClick={() => {
                        setNodeFocused(node);
                        setComponentFocused(props.report.components['-1']);
                      }}
                      log={true}
                    />
                ));

                return (
                  <>
                    <React.Fragment key={`today-${todayKey}`}>
                      {addNodeButton}
                      <span className="log-date">{todayKey}</span>
                      {hasToday && renderNodes(todayEntry[1])}
                    </React.Fragment>

                    {previousEntries.length > 0 && (
                      <button
                        type="button"
                        className="toggle-previous-logs"
                        onClick={() => setShowPreviousLogs(!showPreviousLogs)}
                      >
                        {showPreviousLogs ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        {showPreviousLogs ? 'Hide previous logs' : `Show ${previousEntries.length} previous log date${previousEntries.length > 1 ? 's' : ''}`}
                      </button>
                    )}

                    {showPreviousLogs && previousEntries.map(([date, nodes]) => (
                      <React.Fragment key={date}>
                        <span className="log-date">{date}</span>
                        {renderNodes(nodes)}
                      </React.Fragment>
                    ))}
                  </>
                );
              })()}
            </div>
            )}

            <div className='report-footer'>
              <div className="report-metadata">
                <div className='report-title-container'>
                  {editTitle === 'bottom' ? (
                    <form onSubmit={e => {
                      e.preventDefault();
                      props.updateReport(
                        {id: props.report.id, title: props.report.title}, 
                        {fridge: true, freezer: props.authToken},
                        () => setEditTitle(null)
                      )
                    }}>
                      <input
                        type="text"
                        id="report-title-input"
                        className="title-input"
                        defaultValue={props.report?.title}
                        placeholder="Edit Report Title"
                        onChange={e => props.updateReport(
                          {id: props.report.id, title: e.target.value}, 
                          {fridge: true},
                          null
                        )}
                        onBlur={(e) => {
                          props.updateReport(
                            {id: props.report.id, title: e.target.value}, 
                            {fridge: true, freezer: props.authToken},
                            () => setEditTitle(null)
                          )
                        }}
                      />

                      <CheckIcon className="edit-title submit-title" onClick={e => {
                        props.updateReport(
                          {id: props.report.id, title: e.target.value}, 
                          {fridge: true, freezer: props.authToken},
                          () => setEditTitle(null)
                        )
                      }}/>
                    </form>
                  ) : (
                    <span className="report-title-container" style={{display: editTitle === 'bottom' ? "none" : "flex"}} onClick={() => setEditTitle('bottom')}>
                      <Textfit className="report-title" mode="single" max={16} style={{width: '100%'}}>
                        {props.report?.title}
                      </Textfit>

                      <CreateIcon className="edit-title" />
                    </span>
                  )}
                </div>

                <span className="report-author">
                  {generateAuthorText()}
                </span>

                <form className="onsite-form">
                  <div className="onsite-date">
                    <span>{props.report.operator_report ? 'Reading date' : 'On site'}</span>
                    <div style={{position: 'relative'}}>
                      <input id="onsite-date" className="picker-input datepicker datepicker-footer" value={props.report.date_of_visit}></input>
                      <div className={`saved-indicator ${fieldStates.date_of_visit.isSaved ? 'shown' : ''}`}>
                        <CheckIcon />
                      </div>
                      {fieldStates.date_of_visit.isSaving && <div className="saving-flash shown" style={{width: '100%', zIndex: 1, borderRadius: '10px'}}/>}
                    </div>
                  </div>
                
                  {!props.report.operator_report && <div className="onsite-time">
                    <div style={{position: 'relative'}}>
                      <input 
                        id="onsite-start" 
                        className="picker-input timepicker timepicker-footer" 
                        value={props.report.onsite_start}
                      />
                      <div className={`saved-indicator ${fieldStates.onsite_start.isSaved ? 'shown' : ''}`}>
                        <CheckIcon />
                      </div>
                      {fieldStates.onsite_start.isSaving && <div className="saving-flash shown" style={{width: '100%', zIndex: 1, borderRadius: '10px'}}/>}
                    </div>
                    <span className="onsite-time-sep" aria-hidden="true">–</span>
                    <div style={{position: 'relative'}}>
                      <input 
                        id="onsite-end"
                        className="picker-input timepicker timepicker-footer" 
                        value={props.report.onsite_end} 
                        placeholder=" "
                      />
                      <div className={`saved-indicator ${fieldStates.onsite_end.isSaved ? 'shown' : ''}`}>
                        <CheckIcon />
                      </div>
                      {fieldStates.onsite_end.isSaving && <div className="saving-flash shown" style={{width: '100%', zIndex: 1, borderRadius: '10px'}}/>}
                    </div>
                  </div>}
                </form>
              </div>
              <div className="report-footer-actions">
                <div className="report-buttons-secondary">
                  <a
                    className="sidebar-btn"
                    href={reportViewUrl(props.report.hash_id) || props.report.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <OpenInNewRoundedIcon />
                    Preview
                  </a>
                  <button
                    type="button"
                    className="sidebar-btn"
                    onClick={() => setTempRecipients(props.report.recipients)}
                  >
                    <PeopleAltOutlinedIcon />
                    Recipients
                    {props.report.recipients?.length ? (
                      <span className="sidebar-btn-count">{props.report.recipients.length}</span>
                    ) : null}
                  </button>
                </div>
                <button
                  type="button"
                  className="sidebar-btn sidebar-btn--primary"
                  onClick={() => setPreSubmitShown(true)}
                >
                  <CheckRoundedIcon />
                  Finalize
                </button>
              </div>
            </div>

            {/* spacer if window height doesnt take up full device height */}
            {/* <div style="display:flex; flex-grow: 1"></div> */}
            
          </div>
        </div>

        {tempRecipients && (
          <RecipientSelect
            key={`temp-recipients-${tempRecipients.join(',')}`}
            contacts={[...props.report.contacts]}
            selectedRecipients={tempRecipients}
            handleRecipientSelect={email => {
              setTempRecipients(tempRecipients.includes(email) ? 
                tempRecipients.filter(r => r !== email) :
                [...tempRecipients, email]) 
            }}
            addNewContact={contact => setTempRecipients([...tempRecipients, contact])}
            submit={() => {
              // Compare current and new recipients, ignoring order
              const currentSet = new Set(props.report.recipients || []);
              const newSet = new Set(tempRecipients);
              const hasChanges = currentSet.size !== newSet.size || ![...currentSet].every(recipient => newSet.has(recipient));

              if (hasChanges) {
                props.updateReport({
                  id: props.report.id,
                  recipients: tempRecipients
                }, {fridge: true, freezer: props.authToken}, () => setTempRecipients(null));
              } else {
                setTempRecipients(null);
              }
            }}
            close={() => setTempRecipients(null)}
          />
        )}

        {preSubmitShown && (
          <PreSubmit
            close={() => setPreSubmitShown(false)}
            triggerRecipientSelect={() => setTempRecipients(props.report.recipients)}
            onSuccess={() => setSubmitted(true)}
          />
        )}

        {(componentFocused || nodeFocused) && (
          <NodeWorkbench
            component={componentFocused} 
            node={nodeFocused} 
            initialOption={addIntent}
            switchFocus={(node, comp) => setNodeFocused(node, comp)}
            close={() => {
              setAddIntent(null);
              setComponentFocused(null, setNodeFocused(null));
            }}
          />
        )}

        {submitted && <SubmitSuccess close={() => setSubmitted(false)}/>}
        {changesShown && <ReportChanges reportId={props.report.hash_id} close={() => setChangesShown(false)} />}
      </div>

      {/* <span>{JSON.stringify(componentFocused)}</span> */}
    </>
  )
};

function mapStateToProps(state, props) {
  return {
    authToken: state.authToken,
    report: state.report,
    user: state.user,
    tests: state.testFields,
    loading: state.loading,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportEdit);
